import React, { useState } from 'react';
import { FileText, Plus, Trash2, Route, BookmarkPlus, Download } from 'lucide-react';
import { ITEM_KINDS } from '../../constants/upgrades';
import { api, computeTotals, lineTotal, money, newKey } from '../../utils/upgradesApi';
import UpgradeAiImport from './UpgradeAiImport';

// "Výpis změn a cenová nabídka": what is done at the customer and what it costs.
const UpgradeItemsCard = ({ job, form, set, locked, dirty, templates, onReload, onError }) => {
  const [templateId, setTemplateId] = useState('');
  const [busy, setBusy] = useState(false);
  const totals = computeTotals(form.items, form.discount_percent, job.vat_rate);
  const settings = job.settings || {};
  const hasTravel = form.items.some((i) => i.kind === 'doprava');

  const setItems = (items) => set('items', items);
  const update = (key, patch) => setItems(form.items.map((i) => (i._key === key ? { ...i, ...patch } : i)));
  const remove = (key) => setItems(form.items.filter((i) => i._key !== key));
  const add = (kind) => {
    const base = { _key: newKey(), kind, description: '', qty: 1, unit: 'ks', unit_price: 0 };
    const preset = kind === 'prace' ? { unit: 'h', unit_price: settings.hourly_rate || 0 } : {};
    setItems([...form.items, { ...base, ...preset }]);
  };
  const addFromTemplate = () => {
    const tpl = templates.find((t) => t.id === Number(templateId));
    if (!tpl) return;
    setItems([...form.items, ...tpl.items.map((i) => ({ ...i, _key: newKey(), id: undefined }))]);
    setTemplateId('');
  };

  // Lines confirmed in the AI dialog go into the unsaved form (the user saves with "Uložit změny")
  const applyAiImport = ({ items, replace, description, discount }) => {
    const added = items.map((i) => ({ _key: newKey(), ...i }));
    setItems(replace ? added : [...form.items, ...added]);
    if (description !== undefined) set('description', description);
    if (discount !== undefined) set('discount_percent', discount);
  };

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { onError(e.message); } finally { setBusy(false); }
  };
  const addTravel = () => run(async () => { await api(`/${job.id}/travel`, { method: 'POST' }); await onReload(); });
  const saveAsTemplate = () => {
    const name = window.prompt('Název šablony (např. „Upgrade na tablet“):', job.title);
    if (!name || !name.trim()) return;
    run(async () => { await api(`/${job.id}/save-as-template`, { method: 'POST', body: { name: name.trim() } }); window.alert('Uloženo do šablon. Použijete ji při zakládání nové úpravy.'); });
  };

  return (
    <section className="dlv-card upg-itemsbox" aria-labelledby="upg-items">
      <h2 id="upg-items" className="upg-card-title"><FileText size={18} /> Výpis změn a cenová nabídka
        <small>ceny bez DPH</small></h2>

      {!locked && <UpgradeAiImport jobId={job.id} existingItems={form.items} hasDescription={Boolean(form.description.trim())} onApply={applyAiImport} />}

      {form.items.length === 0 && <p className="dlv-hint">Zatím žádná položka. Přidejte díl, práci nebo vyberte hotovou šablonu.</p>}
      {form.items.length > 0 && (
        <div className="upg-items-head" aria-hidden="true">
          <span>Druh</span><span>Co se udělá</span><span className="upg-num">Množ.</span><span>Jedn.</span><span className="upg-num">Cena / j.</span><span className="upg-num">Celkem</span><span />
        </div>
      )}
      {form.items.map((item, index) => (
        <div className="upg-item" key={item._key}>
          <div className="upg-item__row">
            <label className="upg-cell upg-cell--kind"><span className="upg-cell__cap">Druh</span>
              <select className="dlv-input" aria-label={`Druh položky ${index + 1}`} value={item.kind} disabled={locked} onChange={(e) => update(item._key, { kind: e.target.value })}>
                {ITEM_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </label>
            <label className="upg-cell upg-cell--desc"><span className="upg-cell__cap">Co se udělá</span>
              <input className="dlv-input" aria-label={`Popis změny ${index + 1}`} placeholder="Např. výměna tabletu za novější model" value={item.description} maxLength={500} disabled={locked}
                onChange={(e) => update(item._key, { description: e.target.value })} />
            </label>
            <label className="upg-cell upg-cell--qty"><span className="upg-cell__cap">Množ.</span>
              <input className="dlv-input upg-num" aria-label={`Množství ${index + 1}`} type="number" min="0.01" step="any" value={item.qty} disabled={locked}
                onChange={(e) => update(item._key, { qty: e.target.value })} />
            </label>
            <label className="upg-cell upg-cell--unit"><span className="upg-cell__cap">Jedn.</span>
              <input className="dlv-input" aria-label={`Jednotka ${index + 1}`} value={item.unit} maxLength={10} disabled={locked} onChange={(e) => update(item._key, { unit: e.target.value })} />
            </label>
            <label className="upg-cell upg-cell--price"><span className="upg-cell__cap">Cena / j.</span>
              <input className="dlv-input upg-num" aria-label={`Cena za jednotku ${index + 1}`} type="number" min="0" step="any" value={item.unit_price} disabled={locked}
                onChange={(e) => update(item._key, { unit_price: e.target.value })} />
            </label>
            <div className="upg-cell upg-cell--total"><span className="upg-cell__cap">Celkem</span>
              <span className="upg-item__total" aria-label={`Celkem ${index + 1}`}>{Number(item.unit_price) === 0 ? 'v ceně' : money(lineTotal(item))}</span>
            </div>
            {!locked && <button type="button" className="dlv-iconbtn upg-cell--del" onClick={() => remove(item._key)} aria-label={`Smazat položku ${index + 1}`}><Trash2 size={15} /></button>}
          </div>
        </div>
      ))}

      {!locked && (
        <div className="upg-tools">
          <button type="button" className="dlv-btn upg-sm" onClick={() => add('dil')}><Plus size={16} /> Díl</button>
          <button type="button" className="dlv-btn upg-sm" onClick={() => add('prace')}><Plus size={16} /> Práce</button>
          <button type="button" className="dlv-btn upg-sm" onClick={() => add('jine')}><Plus size={16} /> Jiné</button>
          <button type="button" className="dlv-btn upg-sm" disabled={busy || dirty} onClick={addTravel}
            title={dirty ? 'Nejdřív uložte změny' : 'Vzdálenost se spočítá z adresy zákazníka'}><Route size={16} /> {hasTravel ? 'Přepočítat dopravu' : 'Doprava'}</button>
          {templates.length > 0 && (
            <span style={{ display: 'inline-flex', gap: 8, marginLeft: 'auto' }}>
              <select className="dlv-input" style={{ minWidth: 190 }} aria-label="Šablona změn" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                <option value="">Vložit ze šablony…</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <button type="button" className="dlv-btn upg-sm" disabled={!templateId} onClick={addFromTemplate}>Vložit</button>
            </span>
          )}
        </div>
      )}

      <div className="upg-fields" style={{ marginTop: 18 }}>
        <div>
          <label className="dlv-label" htmlFor="upg-discount">Sleva (%)</label>
          <input id="upg-discount" className="dlv-input" type="number" min="0" max="100" step="any" value={form.discount_percent} disabled={locked}
            onChange={(e) => set('discount_percent', e.target.value)} />
        </div>
        <div>
          <label className="dlv-label" htmlFor="upg-valid">Nabídka platí do</label>
          <input id="upg-valid" className="dlv-input" type="date" value={form.quote_valid_until} disabled={locked} onChange={(e) => set('quote_valid_until', e.target.value)} />
        </div>
      </div>

      <div className="upg-totals" aria-live="polite">
        <div className="upg-totals__row"><span>Mezisoučet</span><span>{money(totals.subtotal)}</span></div>
        {totals.discount > 0 && <div className="upg-totals__row"><span>Sleva {form.discount_percent} %</span><span>− {money(totals.discount)}</span></div>}
        <div className="upg-totals__row"><span>Základ daně</span><span>{money(totals.net)}</span></div>
        <div className="upg-totals__row"><span>DPH {job.vat_rate} %</span><span>{money(totals.vat)}</span></div>
        <div className="upg-totals__row upg-totals__row--gross"><span>Celkem s DPH</span><span>{money(totals.gross)}</span></div>
      </div>

      <div className="upg-tools">
        <a className="dlv-btn upg-sm" href={`/api/upgrades/${job.id}/quote.pdf`} target="_blank" rel="noopener noreferrer"
          aria-disabled={dirty} onClick={(e) => { if (dirty) { e.preventDefault(); onError('Nejdřív uložte změny, PDF se vytváří z uložené nabídky.'); } }}>
          <Download size={16} /> Nabídka (PDF)
        </a>
        <button type="button" className="dlv-btn upg-sm" disabled={busy || dirty || job.items.length === 0} onClick={saveAsTemplate}
          title={dirty ? 'Nejdřív uložte změny' : 'Uložit položky jako šablonu pro příště'}><BookmarkPlus size={16} /> Uložit jako šablonu</button>
      </div>
    </section>
  );
};

export default UpgradeItemsCard;
